import { client } from './api';

/**
 * The staff queue of faxes no mapping could place.
 *
 * Parked faxes are excluded from every agency inbox, so this is the only window
 * onto them and it is Strawhat only. The shape is deliberately not the agency
 * inbox's: there is no receiving number, because the model masks it even for
 * staff and it would not help, and routing keys on the RingCentral ownerId. The
 * subject is served here and nowhere else, since it usually says whose a fax is
 * without anyone opening the document.
 *
 * Every call passes `throwOnError` because the generated client resolves rather
 * than rejects on a 4xx by default, which would show a success toast over a 403.
 */

export interface UnassignedFaxDocument {
  id: string;
  status: string;
  content_type: string | null;
  file_size: number | null;
}

export interface UnassignedFax {
  id: string;
  status: string;
  received_at: string;
  ringcentral_created_at: string | null;
  ringcentral_owner_id: string | null;
  ringcentral_extension_id: string | null;
  /** Null for a fax staff placed by hand, which nothing delivered. */
  ringcentral_message_id: number | null;
  fax_number_from: string | null;
  fax_number_to_source: string;
  subject: string | null;
  page_count: number | null;
}

export interface UnassignedFaxDetail extends UnassignedFax {
  documents: UnassignedFaxDocument[];
}

export interface UnassignedFaxPage {
  items: UnassignedFax[];
  total: number;
}

interface DocumentDownload {
  url: string;
}

export interface AgencyClinicOption {
  id: string;
  name: string;
}

export interface AssignUnassignedFaxBody {
  agency_id: string;
  agency_clinic_id?: string | null;
}

export async function listUnassignedFaxes(
  params: { limit?: number; offset?: number } = {}
): Promise<UnassignedFaxPage> {
  const response = await client.get<UnassignedFaxPage, unknown, true>({
    query: {
      limit: params.limit ?? 25,
      offset: params.offset ?? 0,
    },
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/unassigned-faxes',
  });
  return response.data;
}

export async function getUnassignedFax(
  faxId: string
): Promise<UnassignedFaxDetail> {
  const response = await client.get<UnassignedFaxDetail, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/internal-tools/unassigned-faxes/${faxId}`,
  });
  return response.data;
}

/**
 * A short-lived signed URL for one document of a parked fax.
 *
 * Opened rather than fetched, like the agency-side download: the object is
 * served as an attachment from the bucket's own origin, which is what keeps an
 * HTML cover page from an unknown sender out of our page.
 */
export async function getUnassignedFaxDocumentUrl(
  faxId: string,
  documentId: string
): Promise<string> {
  const response = await client.get<DocumentDownload, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/internal-tools/unassigned-faxes/${faxId}/documents/${documentId}/download`,
  });
  return response.data.url;
}

/**
 * Hand a parked fax to the agency it belongs to.
 *
 * One direction only: the server will not move a fax that already has an
 * agency, so this can rescue a fax from the queue and can never carry one
 * between tenants.
 */
export async function assignUnassignedFax(
  faxId: string,
  body: AssignUnassignedFaxBody
): Promise<UnassignedFax> {
  const response = await client.patch<UnassignedFax, unknown, true>({
    body,
    responseType: 'json',
    throwOnError: true,
    url: `/internal-tools/unassigned-faxes/${faxId}`,
  });
  return response.data;
}

/**
 * The clinics of one agency, for the assign dialog.
 *
 * Not `/agency-clinics`, which answers for the caller's own agency: staff are
 * acting from Strawhat, so that route would always come back empty here.
 */
export async function listAgencyClinics(
  agencyId: string
): Promise<AgencyClinicOption[]> {
  const response = await client.get<AgencyClinicOption[], unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/internal-tools/agencies/${agencyId}/clinics`,
  });
  return response.data;
}
